import { useState, useEffect } from 'react'
import { apiClient } from '@/api/client'
import { mockCoinBalance, mockCoinTransactions } from '@/data/mockCoins'
import type { CoinBalance, CoinTransaction } from '@/data/mockCoins'

export type { CoinBalance, CoinTransaction }

const useMockData = import.meta.env['VITE_USE_MOCK_DATA'] === 'true'
const BALANCE_CACHE_KEY = 'r4s_coin_balance'

function getCachedBalance(): CoinBalance | null {
  try {
    const raw = sessionStorage.getItem(BALANCE_CACHE_KEY)
    return raw ? (JSON.parse(raw) as CoinBalance) : null
  } catch {
    return null
  }
}

export function useCoinBalance() {
  const cached = getCachedBalance()
  const [balance, setBalance] = useState<CoinBalance | null>(cached)
  const [loading, setLoading] = useState(cached === null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (useMockData) {
      setBalance(mockCoinBalance)
      setLoading(false)
      return
    }

    apiClient
      .GET('/api/v1/coins/me/balance')
      .then(({ data, error: apiError }) => {
        if (apiError || !data?.success) throw new Error()
        const bal = data.data ?? null
        setBalance(bal)
        if (bal) sessionStorage.setItem(BALANCE_CACHE_KEY, JSON.stringify(bal))
      })
      .catch(() => {
        if (!cached) setError('Kon het saldo niet ophalen. Probeer het later opnieuw.')
      })
      .finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { balance, loading, error }
}

export function useCoinTransactions() {
  const [transactions, setTransactions] = useState<CoinTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (useMockData) {
      setTransactions(mockCoinTransactions)
      setLoading(false)
      return
    }

    apiClient
      .GET('/api/v1/coins/me', { params: { query: { page: 1, page_size: 50 } } })
      .then(({ data, error: apiError }) => {
        if (apiError || !data?.success) throw new Error()
        setTransactions(data.data?.items ?? [])
      })
      .catch(() => setError('Kon de transacties niet ophalen. Probeer het later opnieuw.'))
      .finally(() => setLoading(false))
  }, [])

  return { transactions, loading, error }
}
